import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { InfoCardInner, LoadingInner, Modal, tableClassNames, TableInner, TDateRange } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { TLogType, useListAllLogsNew } from '../../entities/logs/queries';
import { splitLogFieldName } from '../../entities/logs/shared';
import { useInfiniteScrollQuery } from '../../hooks/InitinityScroll';
import { ActionDetails } from './ActionDetails';
import styles from './Changelog.module.scss';

export const ChangelogNEW = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [logModalInfo, setLogModalInfo] = useState<TLogType>();
  const [companyFilter, setCompanyFilter] = useState<string>();
  const [userTypeFilter, setUserTypeFilter] = useState<string>();
  const [userNameFilter, setUserNameFilter] = useState<string>();
  const [employeeNameFilter, setEmployeeNameFilter] = useState<string>();
  const [occurredAtFilter, setOccurredAtFilter] = useState<TDateRange>();
  const [actionFilter, setActionFilter] = useState<string>();
  const [entityFilter, setEntityFilter] = useState<string>();

  const { data: companies } = useQueryListAllCompaniesOptions();

  const companyFilters = useMemo(() => {
    if (!companies) return [];
    return companies.filter((c) => c.isLive && c.active).map((c) => _.pick(c, ['id', 'name']));
  }, [companies]);

  const {
    data: logs,
    error,
    loading,
    nextToken,
    fetchMore
  } = useListAllLogsNew({
    companyId: companyFilter,
    occurredAt: occurredAtFilter
  });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { sentinelRef } = useInfiniteScrollQuery({
    rootRef: tableContainerRef,
    loading,
    queries: [
      {
        fetchMore: fetchMore.clientFetchMore,
        nextToken: nextToken.clientNextToken
      },
      {
        fetchMore: fetchMore.internalFetchMore,
        nextToken: nextToken.internalNextToken
      }
    ]
  });

  const actionFilters = useMemo(() => {
    return _.uniq(_.map(logs, 'action')).map((a) => ({ label: a as string, value: a as string }));
  }, [logs]);

  const entityFilters = useMemo(() => {
    return _.uniq(_.map(logs, 'entity')).map((e) => ({ label: e as string, value: e as string }));
  }, [logs]);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      disabled: companyFilters.length === 0,
      label: 'Company',
      name: 'companyId',
      onChange: (v) => {
        setCompanyFilter(v != null ? (v as string) : undefined);
      },
      options: companyFilters.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: companyFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'User Type',
      name: 'userType',
      onChange: (v) => {
        setUserTypeFilter(v != null ? (v as string) : undefined);
      },
      options: [
        {
          value: 'client',
          label: 'Client'
        },
        {
          value: 'internal',
          label: 'Internal'
        }
      ],
      value: userTypeFilter
    });

    filtersList.push({
      type: 'datePicker',
      label: 'Occurred At (Range)',
      name: 'occurredAt',
      value: occurredAtFilter,
      range: true,
      onChange: (v) => {
        setOccurredAtFilter(v != null ? (v as TDateRange) : undefined);
      }
    });

    filtersList.push({
      type: 'textInput',
      label: 'Employee Name',
      name: 'employeeName',
      onChange: (v) => {
        setEmployeeNameFilter(v != null ? (v as string) : undefined);
      },
      value: employeeNameFilter
    });

    filtersList.push({
      type: 'textInput',
      label: 'User Name',
      name: 'userName',
      onChange: (v) => {
        setUserNameFilter(v != null ? (v as string) : undefined);
      },
      value: userNameFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Action',
      name: 'action',
      onChange: (v) => {
        setActionFilter(v != null ? (v as string) : undefined);
      },
      options: actionFilters,
      value: actionFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Entity',
      name: 'entity',
      onChange: (v) => {
        setEntityFilter(v != null ? (v as string) : undefined);
      },
      options: entityFilters,
      value: entityFilter
    });

    return filtersList;
  }, [companyFilter, companyFilters, userNameFilter, occurredAtFilter, employeeNameFilter, userTypeFilter, actionFilter, actionFilters, entityFilter, entityFilters]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log: TLogType) => {
      if (userTypeFilter && log.userType !== userTypeFilter) return false;
      if (userNameFilter && userNameFilter !== '' && (log.identityUserName == null || quickScore(log.identityUserName, userNameFilter) < 0.7)) return false;
      if (employeeNameFilter && employeeNameFilter !== '' && (log.employee?.fullName == null || quickScore(log.employee?.fullName, employeeNameFilter) < 0.7)) return false;
      if (actionFilter && log.action !== actionFilter) return false;
      if (entityFilter && log.entity !== entityFilter) return false;
      return true;
    });
  }, [logs, employeeNameFilter, userNameFilter, userTypeFilter, actionFilter, entityFilter]);

  //console.log('logs NEW?:', logs);
  console.log('filteredLogs NEW?:', filteredLogs);

  const getRowId = useCallback((log: TLogType) => {
    return log.id;
  }, []);

  const onRowClick = useCallback((log: TLogType) => {
    setLogModalInfo(log);
    setIsModalOpen(true);
  }, []);

  return (
    <TablePage error={error} title='Changelog (NEW) History (Last 30 Days)'>
      <Filters filters={filters} />
      <div ref={tableContainerRef} style={{ overflow: 'auto' }}>
        <TableInner<TLogType>
          enableSorting={true}
          defaultSorting={{ id: 'occurredAt', desc: true }}
          data={filteredLogs}
          emptyText={logs.length === 0 ? 'No logs were found.' : 'No logs were found with these filters.'}
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          getRowId={getRowId}
          hideSubRowsSelect={true}
          hoverableRows={true}
          selectableRows={false}
          onRowClick={(row) => {
            onRowClick(row.original);
          }}
          virtual={false}
          columns={[
            {
              header: 'Company',
              accessorKey: 'company.name',
              cell: ({ cell }) => <div className={clsx(tableClassNames.tertiary)}>{cell.row.original.company?.name || '-'}</div>
            },
            {
              header: 'Change Date',
              accessorKey: 'occurredAt',
              cell: ({ cell }) => <div className={clsx(tableClassNames.secondary)}>{readableDate(cell.row.original.occurredAt, 'dd MMM yyyy HH:mm')}</div>
            },
            {
              header: 'Employee Name',
              accessorKey: 'employee.fullName',
              cell: ({ cell }: any) => {
                const employee = cell.row.original.employee ?? {};
                const response = cell.row.original.response ?? {};

                const fullName =
                  employee.fullName ||
                  `${employee.firstName || employee.preferredFirstName || ''} ${employee.lastName || ''}`.trim() ||
                  response.fullName ||
                  `${response.firstName || response.preferredFirstName || ''} ${response.lastName || ''}`.trim() ||
                  response.name ||
                  '-';

                return <div className={clsx(tableClassNames.tertiary)}>{fullName}</div>;
              }
            },
            {
              header: 'User Name',
              accessorKey: 'identityUserName',
              cell: ({ cell }) => <div className={clsx(tableClassNames.tertiary)}>{cell.row.original.identityUserName || '-'}</div>
            },
            {
              header: 'User Type',
              accessorKey: 'userType',
              cell: ({ cell }) => <div className={clsx(tableClassNames.tertiary)}>{cell.row.original.userType || '-'}</div>
            },
            {
              header: 'Action',
              accessorKey: 'action',
              cell: ({ cell }) => {
                return <div className={clsx(tableClassNames.tertiary)}>{cell.row.original.action}</div>;
              }
            },
            {
              header: 'Entity',
              accessorKey: 'entity',
              cell: ({ cell }) => {
                return <div className={clsx(tableClassNames.tertiary)}>{cell.row.original.entity}</div>;
              }
            }
          ]}
        />
        {loading && <LoadingInner className={clsx(tableStyles.contentLoader)} />}
        {nextToken && <div ref={sentinelRef} style={{ height: '20px' }} />}
      </div>
      {logModalInfo && (
        <Modal
          headline={`Log (${logModalInfo.fieldName ?? '-'}) in ${logModalInfo.company?.name ?? '-'}`}
          open={isModalOpen}
          size='large'
          closeModal={() => {
            setIsModalOpen(false);
          }}>
          <InfoCardInner<TLogType>
            className={styles.actionDetailsContainer}
            data={logModalInfo}
            sections={[
              {
                title: 'Information',
                columns: [
                  { label: 'Log ID', render: (data) => data?.id },
                  {
                    label: 'Change Date',
                    render: (data) => readableDate(data?.occurredAt, 'do MMM, yyyy hh:mmaaa')
                  },
                  {
                    label: 'Company',
                    render: (data) => data?.company?.name
                  },
                  {
                    label: 'Employee Name',
                    render: (data) => {
                      const employee = data?.employee;
                      const response = data?.response;

                      const fullName =
                        employee?.fullName ||
                        `${employee?.firstName || employee?.preferredFirstName || ''} ${employee?.lastName || ''}`.trim() ||
                        response.fullName ||
                        `${response.firstName || response.preferredFirstName || ''} ${response.lastName || ''}`.trim() ||
                        response.name ||
                        '-';

                      return fullName;
                    }
                  },
                  {
                    label: 'User Name',
                    render: (data) => data?.identityUserName
                  },
                  {
                    label: 'User Email',
                    render: (data) => data?.identityUserEmail
                  },
                  {
                    label: 'Action',
                    render: (data) => splitLogFieldName(data?.fieldName || '').join(' ')
                  },
                  {
                    label: 'Resource ID',
                    render: (data) => data?.resourceId
                  }
                ]
              }
            ]}
          />
          <InfoCardInner
            className={styles.actionDetailsContainer}
            data={{}}
            sections={[
              {
                title: 'Action Details (Inputted Values)',
                columns: Object.entries(logModalInfo.variables.input || {}).map(([key, value]: [string, any]) => {
                  return {
                    label: `${key}:`,
                    render: () => <ActionDetails input={value} />
                  };
                })
              }
            ]}
          />
        </Modal>
      )}
    </TablePage>
  );
};
